import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-[color,background-color,border-color,opacity,transform] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] disabled:pointer-events-none disabled:opacity-40",
  {
    variants: {
      variant: {
        default:
          "bg-ui-accent text-ui-on-accent shadow-lg hover:bg-ui-accent-hover active:bg-[var(--ui-accent-press)] active:scale-[0.98] dark:shadow-violet-900/30",
        secondary: "bg-ui-surface-raised text-ui-text-primary border border-ui-border hover:bg-ui-surface-sunken",
        ghost: "text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary",
        outline:
          "border border-ui-border-strong bg-transparent text-ui-text-primary hover:bg-ui-surface-raised hover:text-ui-text-primary",
        danger: "bg-ui-danger text-ui-on-accent hover:bg-[var(--ui-danger-hover)]",
        warning:
          "bg-ui-warning text-ui-on-warning shadow-lg hover:bg-[var(--ui-warning-hover)] active:bg-[var(--ui-warning-press)] active:scale-[0.98]",
      },
      size: {
        default: "h-10 px-4 py-2",
        xs: "h-7 gap-1 rounded-md px-2 text-[11px]",
        sm: "h-8 rounded-lg px-3 text-xs",
        lg: "h-12 rounded-lg px-6 text-base",
        icon: "h-9 w-9 rounded-lg",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
)

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & { asChild?: boolean }

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
  },
)
Button.displayName = "Button"

export function Card({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "rounded-lg border border-ui-border p-5 shadow-[var(--ui-shadow-card)] backdrop-blur-md dark:bg-zinc-900/40 dark:shadow-xl dark:shadow-black/20 light:bg-ui-surface light:shadow-[var(--shadow-clay-inset),var(--ui-shadow-sm)]",
        className,
      )}
      {...props}
    />
  )
}

const badgeVariants = cva("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium", {
  variants: {
    tone: {
      neutral:
        "border-[var(--ui-badge-neutral-border)] bg-[var(--ui-badge-neutral-bg)] text-[var(--ui-badge-neutral-fg)]",
      violet: "border-[var(--ui-badge-violet-border)] bg-[var(--ui-badge-violet-bg)] text-[var(--ui-badge-violet-fg)]",
      emerald:
        "border-[var(--ui-badge-emerald-border)] bg-[var(--ui-badge-emerald-bg)] text-[var(--ui-badge-emerald-fg)]",
      amber: "border-[var(--ui-badge-amber-border)] bg-[var(--ui-badge-amber-bg)] text-[var(--ui-badge-amber-fg)]",
      rose: "border-[var(--ui-badge-rose-border)] bg-[var(--ui-badge-rose-bg)] text-[var(--ui-badge-rose-fg)]",
      sky: "border-[var(--ui-badge-sky-border)] bg-[var(--ui-badge-sky-bg)] text-[var(--ui-badge-sky-fg)]",
    },
  },
  defaultVariants: { tone: "neutral" },
})

export type BadgeProps = React.HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>

export function Badge({ className, tone, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />
}

/** Мелкий бейдж в строке таблицы или карточки: 10 px, скруглённый прямоугольник. */
export const BADGE_SM_CLASS = "shrink-0 rounded-md px-1.5 py-px text-[10px] leading-[15px] font-normal"

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        "flex h-10 w-full rounded-lg border border-ui-border bg-ui-surface-sunken px-3 text-base text-ui-text-primary placeholder:text-ui-text-muted focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none sm:text-sm",
        className,
      )}
      {...props}
    />
  ),
)
Input.displayName = "Input"

export const Checkbox = React.forwardRef<HTMLInputElement, Omit<React.InputHTMLAttributes<HTMLInputElement>, "type">>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      type="checkbox"
      className={cn("h-4 w-4 rounded border-ui-border-strong bg-ui-surface-sunken", className)}
      {...props}
    />
  ),
)
Checkbox.displayName = "Checkbox"

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, ...props }, ref) => (
    <textarea
      ref={ref}
      className={cn(
        "flex min-h-[120px] w-full rounded-lg border border-ui-border bg-ui-surface-sunken px-3 py-2 text-base text-ui-text-primary placeholder:text-ui-text-muted focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none sm:text-sm",
        className,
      )}
      {...props}
    />
  ),
)
Textarea.displayName = "Textarea"

export function Skeleton({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <div className={cn("animate-pulse rounded-lg bg-ui-surface-raised", className)} style={style} aria-hidden />
}

/** Shortcut hint inside buttons — explicit text color so it stays readable on accent backgrounds. */
export function Kbd({ className, ...props }: React.HTMLAttributes<HTMLElement>) {
  return (
    <kbd
      className={cn(
        "hidden rounded border border-ui-border-strong bg-ui-bg-elevated px-1 font-sans font-medium text-ui-text-primary sm:inline",
        className,
      )}
      {...props}
    />
  )
}

export function DialogSurface({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "rounded-lg border border-ui-border bg-ui-surface shadow-[var(--ui-shadow-card)] dark:bg-ui-surface-ink dark:shadow-2xl light:shadow-[var(--shadow-clay-inset),var(--ui-shadow-md)]",
        className,
      )}
      {...props}
    />
  )
}
